/*
 * Candidate THEME script, not a Custom Pixel.
 *
 * BRIDGE VERSION 2 (Wave 2 continuity). Changes vs v1:
 *   - GN_StampVersion '1' -> '2'. The cart attribute set is otherwise unchanged:
 *     it still stamps ONLY GN_VisitorId, GN_SessionId, GN_StampVersion.
 *     (No GN_CartToken: Shopify's orders webhook already carries the order's
 *     cart_token, so a duplicate attribute adds no deterministic evidence.)
 *   - After reading /cart.js (which v1 already did), it may send ONE internal
 *     CartContinuity observation to shadow-edge when the material continuity
 *     state changed: a new cart token, a new session for an existing cart, a
 *     changed stamp, or checkout intent with unsent state. No Meta event fires.
 *   - Only the cart token's public part is sent. Shopify's /cart.js token is
 *     "<token>?key=<secret>"; everything from "?" on is dropped and never sent.
 *   - Empty carts are not reported (a token on an empty cart is not a cart
 *     observation).
 *
 * Never reads/writes/removes EdgeTag*, GN_EdgeTag*, or any other merchant
 * attributes. Every network step is best-effort: it never blocks Add to Cart,
 * checkout, Shop Pay, Apple Pay or a native cart form submission.
 */
(function () {
  const KEYS = ['GN_VisitorId', 'GN_SessionId', 'GN_StampVersion'];
  const STAMP_VERSION = '2';
  const BRIDGE_VERSION = 'cart-bridge-2';
  const MARK = 'data-gn-shadow-stamp';
  const CONTINUITY_ENDPOINT = 'https://shadow-edge.goodsnova.com/v1/continuity';
  const SENT_KEY = 'gn_shadow_cc_sent';
  const MIN_READ_INTERVAL_MS = 2000;
  const NETWORK_TIMEOUT_MS = 4000;
  function cookie(name) {
    for (const entry of document.cookie.split(';')) {
      const index = entry.indexOf('=');
      if (index < 0 || entry.slice(0, index).trim() !== name) continue;
      try { return decodeURIComponent(entry.slice(index + 1).trim()); } catch { return ''; }
    }
    return '';
  }
  function attributes() {
    if (window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() !== true) return null;
    const visitor = cookie('gn_shadow_vid');
    const session = cookie('gn_shadow_sid');
    if (!/^vis_[a-zA-Z0-9_-]{8,100}$/.test(visitor) || !/^ses_[a-zA-Z0-9_-]{8,100}$/.test(session)) return null;
    return { GN_VisitorId: visitor, GN_SessionId: session, GN_StampVersion: STAMP_VERSION };
  }
  function root() {
    const route = window.Shopify?.routes?.root;
    return typeof route === 'string' && /^\/(?!\/)/.test(route) ? route.replace(/\/?$/, '/') : '/';
  }
  // Shopify's public cart token only; the "?key=" access secret never leaves the page.
  function publicCartToken(token) {
    if (typeof token !== 'string') return null;
    const base = token.split('?')[0];
    return /^[A-Za-z0-9_-]{8,100}$/.test(base) ? base : null;
  }
  function withTimeout(url, init) {
    const options = Object.assign({}, init);
    let timer = null;
    if (typeof AbortController === 'function') {
      const controller = new AbortController();
      options.signal = controller.signal;
      timer = setTimeout(() => { try { controller.abort(); } catch {} }, NETWORK_TIMEOUT_MS);
    }
    return fetch(url, options).finally(() => { if (timer) { try { clearTimeout(timer); } catch {} } });
  }
  function readSent() {
    try { return window.sessionStorage?.getItem(SENT_KEY) || ''; } catch { return ''; }
  }
  function writeSent(value) {
    try { window.sessionStorage?.setItem(SENT_KEY, value); } catch {}
  }
  let lastSent = '';
  function continuityState(values, cartToken) {
    return [cartToken, values.GN_VisitorId, values.GN_SessionId, values.GN_StampVersion].join('|');
  }
  function sendContinuity(values, cartToken, reason) {
    const state = continuityState(values, cartToken);
    if (state === lastSent || state === readSent()) { lastSent = state; return; }
    lastSent = state;
    writeSent(state);
    const random = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID().replace(/-/g, '')
      : String(Date.now()) + Math.random().toString(36).slice(2);
    const body = JSON.stringify({
      event_name: 'CartContinuity',
      event_id: 'evt_' + random.slice(0, 32),
      occurred_at: new Date().toISOString(),
      visitor_id: values.GN_VisitorId,
      session_id: values.GN_SessionId,
      cart_token: cartToken,
      bridge_version: BRIDGE_VERSION,
      stamp_version: values.GN_StampVersion,
      reason,
      consent: { analytics: true }
    });
    // text/plain avoids a CORS preflight; keepalive survives checkout navigation.
    withTimeout(CONTINUITY_ENDPOINT, {
      method: 'POST', keepalive: true, credentials: 'omit',
      headers: { 'Content-Type': 'text/plain' }, body
    }).catch(() => { lastSent = ''; writeSent(''); });
  }
  function syncCartForms(values) {
    if (!document.querySelectorAll) return;
    document.querySelectorAll('form').forEach(form => {
      let isCart = false;
      try {
        const action = new URL(form.action, window.location.href);
        isCart = action.origin === window.location.origin && /\/cart\/?$/.test(action.pathname);
      } catch {}
      if (!isCart) return;
      for (const key of KEYS) {
        const name = 'attributes[' + key + ']';
        let input = form.querySelector('input[name="' + name + '"]');
        // An existing merchant/app input is never overwritten or removed.
        if (input && input.getAttribute(MARK) !== '1') continue;
        if (!values) {
          input?.remove();
          continue;
        }
        if (!input) {
          input = document.createElement('input');
          input.type = 'hidden'; input.name = name;
          input.setAttribute(MARK, '1'); form.appendChild(input);
        }
        input.value = values[key];
      }
    });
  }
  let inFlight = false;
  let lastRead = 0;
  async function stamp(reason, force) {
    const values = attributes();
    syncCartForms(values);
    if (inFlight || !values) return;
    const now = Date.now();
    if (!force && now - lastRead < MIN_READ_INTERVAL_MS) return;
    inFlight = true;
    lastRead = now;
    try {
      const route = root();
      const cartResponse = await withTimeout(route + 'cart.js', { credentials: 'same-origin', cache: 'no-store' });
      if (!cartResponse.ok) return;
      const cart = await cartResponse.json();
      const token = publicCartToken(cart?.token);
      const stamped = Object.entries(values).every(([key, value]) => cart.attributes?.[key] === value);
      if (!stamped) {
        // Shopify Ajax Cart updates named attributes. Only our three keys are sent.
        const response = await withTimeout(route + 'cart/update.js', {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: values })
        });
        if (!response.ok) throw new Error('Cart stamp failed');
      }
      if (token && Number(cart?.item_count) > 0) sendContinuity(values, token, reason || 'observed');
    } finally { inFlight = false; }
  }
  function run(reason, force) {
    try { stamp(reason, force).catch(() => {}); } catch {}
  }
  // Pixel cookie writes can occur after the theme script loads.
  [0, 500, 2000, 5000].forEach(ms => setTimeout(() => run('page', ms === 0), ms));
  document.addEventListener('visitorConsentCollected', () => run('consent', true));
  document.addEventListener('shopifyCustomerPrivacyApiLoaded', () => run('consent', true));
  document.addEventListener('submit', () => run('submit', true), true);
  document.addEventListener('click', event => {
    try {
      if (event.target?.closest?.('a[href*="/checkout"], [name="checkout"], button[type="submit"]')) run('checkout_intent', true);
    } catch {}
  }, true);
  document.addEventListener('visibilitychange', () => {
    try { if (document.visibilityState === 'hidden') run('page_hidden', false); } catch {}
  });
})();